import moment from 'moment';
import { PropertyViewModel } from '../property-view-model';
import { PropertyViewModelInitializationInfo } from '../property-view-model-initialization-info';
import { BigNumber, CompareModes, DateTime, OpenDirections } from '@nts/std/types';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { DateTimeMetaData } from '../../meta-data/date-time-meta-data';
import { ViewModelInterface } from '@nts/std/interfaces';

export abstract class BaseDateTimePropertyViewModel extends PropertyViewModel<Date> {

    private internalShowTime = false;
    showTime$ = new BehaviorSubject<boolean>(this.internalShowTime);
    advancingCaret = true;

    get showTime(): boolean {
        return this.internalShowTime;
    }
    set showTime(value: boolean) {
        if (this.internalShowTime !== value) {
            this.internalShowTime = value;
            this.onPropertyChanged('showTime');
            this.showTime$.next(value);
        }
    }

    #compareMode: CompareModes | null = null;
    compareMode$: BehaviorSubject<CompareModes | null> = new BehaviorSubject<CompareModes | null>(this.#compareMode);

    get compareMode(): CompareModes | null {
        return this.#compareMode;
    }
    set compareMode(value: CompareModes | null) {
        if (this.#compareMode !== value) {
            this.#compareMode = value;
            this.onPropertyChanged('compareMode');
            this.compareMode$.next(value);
        }
    }

    #comparePropertyName: string | null = null;
    comparePropertyName$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(this.#comparePropertyName);

    get comparePropertyName(): string | null {
        return this.#comparePropertyName;
    }
    set comparePropertyName(value: string | null) {
        if (this.#comparePropertyName !== value) {
            this.#comparePropertyName = value;
            this.onPropertyChanged('comparePropertyName');
            this.comparePropertyName$.next(value);
        }
    }

    #openDirection: OpenDirections | null = null;
    openDirection$: BehaviorSubject<OpenDirections | null> = new BehaviorSubject<OpenDirections | null>(this.#openDirection);

    get openDirection(): OpenDirections | null {
        return this.#openDirection;
    }
    set openDirection(value: OpenDirections | null) {
        if (this.#openDirection !== value) {
            this.#openDirection = value;
            this.onPropertyChanged('openDirection');
            this.openDirection$.next(value);
        }
    }

    #minValue: Date | null = null;
    minValue$: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(this.#minValue);
    get minValue(): Date | null {
        return this.#minValue;
    }
    set minValue(value: Date | null) {
        if (this.#minValue !== value) {
            this.#minValue = value;
            this.onPropertyChanged('minValue');
            this.minValue$.next(value);
        }
    }

    #maxValue: Date | null = null;
    maxValue$: BehaviorSubject<Date | null> = new BehaviorSubject<Date | null>(this.#maxValue);
    get maxValue(): Date | null {
        return this.#maxValue;
    }
    set maxValue(value: Date | null) {
        if (this.#maxValue !== value) {
            this.#maxValue = value;
            this.onPropertyChanged('maxValue');
            this.maxValue$.next(value);
        }
    }

    private readonly MASKDATEANDTIME: string = 'g'; // General date/time pattern(short time)
    private readonly MASKDATE_DEFAULT: string = 'd'; // d	Short date pattern
    private _editMask: string;


    get editMask(): string {
        if (this._editMask) {
            return this._editMask;
        }

        if (this.showTime) {
            return this.MASKDATEANDTIME;
        } else {
            return this.MASKDATE_DEFAULT;
        }
    }
    set editMask(value: string) {
        this._editMask = value;
    }

    setDefaultValueFromLayoutMetaData(value: string | number | BigNumber | boolean) {
        // TODO
    }

    constructor(initInfo: PropertyViewModelInitializationInfo, isNullable: boolean) {
        super(initInfo, isNullable);
        this.compareMode = (initInfo.propertyMetaData as DateTimeMetaData).compareMode;
        this.comparePropertyName = (initInfo.propertyMetaData as DateTimeMetaData).comparePropertyName;
        this.openDirection = (initInfo.propertyMetaData as DateTimeMetaData).openDirection;
        this.minValue = (initInfo.propertyMetaData as DateTimeMetaData).minValue as Date;
        this.maxValue = (initInfo.propertyMetaData as DateTimeMetaData).maxValue as Date;

        if (this.comparePropertyName != null) {
            this.subscribeToComparePropertyChanges(this.comparePropertyName);
        }
    }

    private subscribeToComparePropertyChanges(comparePropertyName: string) {
        const parentViewModel = this.parent;

        const subscriptionFn = () => new Promise<void>((resolve) => {
            const comparePropertyViewModel = parentViewModel.getProperty(comparePropertyName);

            if (comparePropertyViewModel) {
                comparePropertyViewModel
                    .propertyChanged
                    .pipe(
                        takeUntil(this.destroySubscribers$)
                    )
                    .subscribe(() => {
                        this.validate();
                    });
            }

            resolve();
        });

        parentViewModel.appendToPostInit(subscriptionFn);
    }

    /**
     * Ritorna il valore minimo calcolato per questa proprietà in base alla proprietà di confronto e al minimo
     * valore.
     */
    getComputedMinValue(): Date | null {
        if (this.compareMode == null && this.minValue == null) {
            return null;
        }

        let minValue: Date | null = this.minValue;

        if (this.compareMode != null) {
            const compareProperty = this.parent as ViewModelInterface;
            const comparePropertyViewModel = compareProperty?.getProperty(this.comparePropertyName);
            if (comparePropertyViewModel == null || comparePropertyViewModel.value == null) {
                return minValue;
            }

            if (this.compareMode === CompareModes.GreaterThanOrEqualTo) {
                const minDateFromCompareMode = comparePropertyViewModel.value ? moment(comparePropertyViewModel.value).toDate() : null;
                return (minValue && minValue > minDateFromCompareMode) ? minValue : minDateFromCompareMode;
            }

            if (this.compareMode === CompareModes.GreaterThan) {
                const minDateFromCompareMode = comparePropertyViewModel.value ? moment(comparePropertyViewModel.value).add(1, 'days').toDate() : null;
                return (minValue && minValue > minDateFromCompareMode) ? minValue : minDateFromCompareMode;
            }
        }

        return minValue;
    }

    /**
     * Ritorna il valore massimo calcolato per questa proprietà in base alla proprietà di confronto e al massimo
     * valore.
     */
    getComputedMaxValue(): Date | null {
        if (this.compareMode == null && this.maxValue == null) {
            return null;
        }

        let maxValue: Date | null = this.maxValue;
        if (this.compareMode != null) {
            const compareProperty = this.parent as ViewModelInterface;
            const comparePropertyViewModel = compareProperty?.getProperty(this.comparePropertyName);
            if (comparePropertyViewModel == null || comparePropertyViewModel.value == null) {
                return maxValue;
            }

            if (this.compareMode === CompareModes.LessThanOrEqualTo) {
                const maxDateFromCompareMode = comparePropertyViewModel.value ? moment(comparePropertyViewModel.value).toDate() : null;
                return (maxValue && maxValue < maxDateFromCompareMode) ? maxValue : maxDateFromCompareMode;
            }

            if (this.compareMode === CompareModes.LessThan) {
                const maxDateFromCompareMode = comparePropertyViewModel.value ? moment(comparePropertyViewModel.value).subtract(1, 'days').toDate() : null;
                return (maxValue && maxValue < maxDateFromCompareMode) ? maxValue : maxDateFromCompareMode;
            }
        }

        return maxValue;
    }
}

export class DateTimePropertyViewModel extends BaseDateTimePropertyViewModel {

    constructor(initInfo: PropertyViewModelInitializationInfo) {
        super(initInfo, false);
        this.setFaultBackValueFunc(() => new Date());
    }

    override async resetValue(useDefaultValue: boolean): Promise<void> {
        if (useDefaultValue) {
            await this.setValueAsync(new Date());
        } else {
            await this.setValueAsync(this.faultBackValue);
        }
    }

    protected getFormattedValue(value: Date) {
        if (!value) {
            return '';
        }

        if (this.value == null) { return ''; }
        return DateTime.getFormattedValue(value, this.showTime);
    }
}

export class NDateTimePropertyViewModel extends BaseDateTimePropertyViewModel {

    constructor(initInfo: PropertyViewModelInitializationInfo) {
        super(initInfo, true);
        this.isNullable = true;
    }

    override async resetValue(useDefaultValue: boolean): Promise<void> {
        if (useDefaultValue) {
            await this.setValueAsync(new Date());
        } else {
            await this.setValueAsync(this.faultBackValue);
        }
    }

    protected getFormattedValue(value: Date) {
        if (!value) {
            return '';
        }

        if (this.value == null) { return ''; }
        let formattedValue = moment(this.value).format('L');
        if (this.showTime) {
            formattedValue += ' ' + moment(this.value).format('LT');
        }
        return formattedValue;
    }
}
